import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from "@mui/material";
import { fmt, importarPresupuestosLote, MESES } from "../services/presupuestoService";

const TAM_LOTE = 500;
const CLAVES = ["nombre", "concepto", "id_macrocuenta", "cuenta", "nro_contrato", "moneda", "anio", ...MESES, "observacion"];
const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };

const normalizar = (h) => String(h ?? "").replace(/^﻿/, "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, "");
const MAPA = Object.fromEntries(CLAVES.map((k) => [normalizar(k), k]));

const aNumero = (v) => {
  const s = String(v ?? "").trim().replace(/[, ]/g, "");
  if (s === "") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const detectarDelimitador = (linea) => {
  const cuentas = { ",": 0, ";": 0, "\t": 0 };
  Object.keys(cuentas).forEach((d) => { cuentas[d] = linea.split(d).length - 1; });
  return Object.entries(cuentas).sort((a, b) => b[1] - a[1])[0][0] || "\t";
};

// CSV mínimo con comillas ("" = comilla literal) y saltos de línea dentro de comillas.
const parsearCsv = (texto, delim) => {
  const filas = [];
  let fila = [];
  let campo = "";
  let comillas = false;
  for (let i = 0; i < texto.length; i++) {
    const c = texto[i];
    if (comillas) {
      if (c === '"' && texto[i + 1] === '"') { campo += '"'; i++; }
      else if (c === '"') comillas = false;
      else campo += c;
    } else if (c === '"') comillas = true;
    else if (c === delim) { fila.push(campo); campo = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && texto[i + 1] === "\n") i++;
      fila.push(campo); campo = "";
      filas.push(fila); fila = [];
    } else campo += c;
  }
  if (campo !== "" || fila.length) { fila.push(campo); filas.push(fila); }
  return filas;
};

const PLANTILLA = [
  CLAVES.join(","),
  "Presupuesto 1,Gastos Operativos,1,12345,C-001,Bolivianos,2025,1000,1500,1200,1100,900,950,800,700,650,500,400,300,Presupuesto inicial aprobado",
].join("\n");

// Importación masiva desde CSV (espejo de presupuestos_bulk_import.php): se valida, se muestra una vista
// previa con los totales por mes y se envía al servidor en lotes de 500 filas.
export default function PresupuestoImportDialog({ abierto, onClose, onImportado }) {
  const [archivo, setArchivo] = useState(null);
  const [filas, setFilas] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [reporte, setReporte] = useState([]);

  useEffect(() => {
    if (abierto) { setArchivo(null); setFilas(null); setResumen(null); setError(""); setReporte([]); }
  }, [abierto]);

  const leer = async (file) => {
    setArchivo(file); setFilas(null); setResumen(null); setError(""); setReporte([]);
    if (!file) return;
    const texto = await file.text();
    if (!texto.trim()) { setError("El archivo parece estar vacío."); return; }
    const delim = detectarDelimitador(texto.split(/\r?\n/)[0]);
    const tabla = parsearCsv(texto, delim);
    const encabezados = (tabla[0] || []).map((h) => MAPA[normalizar(h)] || String(h).trim());
    const faltan = CLAVES.filter((k) => !encabezados.includes(k));
    if (faltan.length) { setError(`Faltan columnas requeridas: ${faltan.join(", ")}.`); return; }

    let total = 0;
    let omitidas = 0;
    const validas = [];
    tabla.slice(1).forEach((r) => {
      if (r.length === 1 && r[0].trim() === "") return;
      const raw = {};
      encabezados.forEach((h, i) => { raw[h] = r[i] ?? ""; });
      const item = {
        nombre: String(raw.nombre).trim(), concepto: String(raw.concepto).trim(), id_macrocuenta: Math.trunc(aNumero(raw.id_macrocuenta)),
        cuenta: String(raw.cuenta).trim(), nro_contrato: String(raw.nro_contrato).trim(), moneda: String(raw.moneda).trim(),
        anio: Math.trunc(aNumero(raw.anio)), observacion: String(raw.observacion).trim(),
      };
      MESES.forEach((m) => { item[m] = aNumero(raw[m]); });
      total++;
      if (!item.nombre || !item.concepto || !item.anio) { omitidas++; return; }
      validas.push(item);
    });
    if (!validas.length) { setError("No se encontraron filas válidas después de validar (se exige nombre, concepto y año)."); return; }
    const totales = Object.fromEntries(MESES.map((m) => [m, validas.reduce((a, it) => a + it[m], 0)]));
    setFilas(validas);
    setResumen({ total, validas: validas.length, omitidas, totales });
  };

  const importar = async () => {
    setEnviando(true); setError(""); setReporte([]);
    const lotes = [];
    for (let i = 0; i < filas.length; i += TAM_LOTE) lotes.push(filas.slice(i, i + TAM_LOTE));
    const rep = [];
    for (let i = 0; i < lotes.length; i++) {
      try {
        await importarPresupuestosLote(lotes[i]);
        rep.push({ lote: `${i + 1}/${lotes.length}`, filas: lotes[i].length, ok: true, detalle: "Importado" });
      } catch (e) {
        rep.push({ lote: `${i + 1}/${lotes.length}`, filas: lotes[i].length, ok: false, detalle: e?.response?.data?.error || e?.response?.data?.message || "Error" });
      }
      setReporte([...rep]);
    }
    setEnviando(false);
    if (rep.some((r) => r.ok)) onImportado();
  };

  const descargarPlantilla = () => {
    const url = URL.createObjectURL(new Blob([PLANTILLA], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "plantilla_presupuestos.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const terminado = reporte.length > 0 && !enviando;
  return (
    <Dialog open={abierto} onClose={enviando ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Importar presupuestos (CSV)</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
          <Button size="small" variant="outlined" component="label" disabled={enviando}>
            {archivo ? archivo.name : "Seleccionar archivo (.csv, .tsv, .txt)"}
            <input hidden type="file" accept=".csv,.tsv,.txt" onChange={(e) => leer(e.target.files?.[0] || null)} />
          </Button>
          <Button size="small" onClick={descargarPlantilla}>Descargar plantilla CSV</Button>
        </Box>
        <Typography sx={{ fontSize: 11 }} color="text.secondary">
          Columnas requeridas: {CLAVES.join(", ")}. Se omiten las filas sin nombre, concepto o año.
        </Typography>

        {resumen && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>
              {resumen.validas} fila(s) válida(s) de {resumen.total}{resumen.omitidas ? `, ${resumen.omitidas} omitida(s)` : ""}. Vista previa de las primeras 50:
            </Typography>
            <TableContainer sx={{ maxHeight: "42vh" }}>
              <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    {["Nombre", "Concepto", "Macro", "Cuenta", "Contrato", "Moneda", "Año", ...MESES.map((m) => m.slice(0, 3)), "Observación"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filas.slice(0, 50).map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{r.nombre}</TableCell><TableCell>{r.concepto}</TableCell><TableCell>{r.id_macrocuenta}</TableCell><TableCell>{r.cuenta}</TableCell>
                      <TableCell>{r.nro_contrato}</TableCell><TableCell>{r.moneda}</TableCell><TableCell>{r.anio}</TableCell>
                      {MESES.map((m) => <TableCell key={m} align="right">{fmt(r[m])}</TableCell>)}
                      <TableCell>{r.observacion}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell colSpan={7} sx={{ fontWeight: 800 }} align="right">Totales (todas las filas válidas)</TableCell>
                    {MESES.map((m) => <TableCell key={m} align="right" sx={{ fontWeight: 800 }}>{fmt(resumen.totales[m])}</TableCell>)}
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {reporte.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>Resultado de la importación</Typography>
            <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
              <TableHead><TableRow><TableCell sx={{ fontWeight: 800 }}>Lote</TableCell><TableCell sx={{ fontWeight: 800 }} align="right">Filas</TableCell><TableCell sx={{ fontWeight: 800 }}>Estado</TableCell><TableCell sx={{ fontWeight: 800 }}>Detalle</TableCell></TableRow></TableHead>
              <TableBody>
                {reporte.map((r) => (
                  <TableRow key={r.lote}><TableCell>{r.lote}</TableCell><TableCell align="right">{r.filas}</TableCell>
                    <TableCell sx={{ color: r.ok ? "success.main" : "error.main", fontWeight: 700 }}>{r.ok ? "OK" : "Error"}</TableCell><TableCell>{r.detalle}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={enviando}>Cerrar</Button>
        {resumen && !terminado && (
          <Button size="small" variant="contained" disabled={enviando} onClick={importar}
            startIcon={enviando ? <CircularProgress size={14} color="inherit" /> : null}>
            Importar {resumen.validas} fila(s)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
